/**
 * The foundations chapter's translation between the pinned client and the §8 components.
 *
 * `12` §2.4 control 2 says `bata-ui` never hand-rolls a request; it does not say the API's
 * response models and the design system's component contracts are the same types. They
 * are not, and the difference is where the honest work is:
 *
 *   * `NodeSummary` is a FLAT list with a `parentId`; `HierarchyNavigator` takes a tree;
 *   * `AlertSummary` has a free-text `severity`; `Exception` has a four-value threshold
 *     class that drives an icon and a colour, and §12.6 makes both mandatory;
 *   * `FeedStatus` reports `certified`, `blocking` and `freshnessHours`;
 *     `FeedCertification` reports one of four states and two timestamps.
 *
 * Every mapping below is one of three things, and each is labelled:
 *
 *   TRANSFORM  a shape change that loses nothing — a flat list into a tree
 *   DERIVE     a value computed from what the contract does carry, stated as such
 *   GAP        a field the component requires that the contract does not expose
 *
 * The GAPs are listed in `docs/adr/0014` and are what the next spec version has to close.
 * None of them is filled with an invented measurement: where the contract is silent the
 * screen says so, because a plausible number is worse than a visible gap (G2.2).
 */

import type {
  AlertSummary,
  FeedStatus,
  NodeSummary,
  ScenarioSummary,
  SchemeSummary,
} from '@/api/generated';
import { formatNumber } from '@/lib/format';
import type {
  Exception,
  FeedCertification,
  FeedState,
  HierarchyNode,
  Lineage,
  ScenarioContext,
  ThresholdClass,
} from '@/planning';
import { EXCEPTION_RULE_LABEL_KEY } from '@/planning';

// ── hierarchy ────────────────────────────────────────────────────────────────

/** Cap on tree depth, so a cyclic edge set is a bounded render rather than a hang. */
const MAX_DEPTH = 24;

/**
 * TRANSFORM — the flat node list into the navigator's tree.
 *
 * `NodeSummary` carries `parentId`, and the platform's docstring says depth is computed
 * and never a column (G5), so the tree is built here rather than requested. A node whose
 * parent is not in the page is treated as a root: a partially-loaded hierarchy must
 * render what it has rather than silently drop a subtree.
 *
 * GAP — `NodeSummary` has no display name. `naturalKey` is the business key and it is
 * what the label shows; a name would be reference data, not a catalogue key (G6).
 */
export function toHierarchyTree(nodes: readonly NodeSummary[]): readonly HierarchyNode[] {
  const byParent = new Map<string, NodeSummary[]>();
  const ids = new Set(nodes.map((node) => node.nodeId));

  for (const node of nodes) {
    const parent = node.parentId !== undefined && node.parentId !== null && ids.has(node.parentId)
      ? node.parentId
      : '';
    const siblings = byParent.get(parent);
    if (siblings) siblings.push(node);
    else byParent.set(parent, [node]);
  }

  const build = (node: NodeSummary, depth: number): HierarchyNode => {
    const children = depth >= MAX_DEPTH ? [] : (byParent.get(node.nodeId) ?? []);
    const built = children.map((child) => build(child, depth + 1));
    return {
      id: node.nodeId,
      label: node.naturalKey,
      level: node.nodeType,
      childCount: built.length,
      ...(built.length > 0 ? { children: built } : {}),
    };
  };

  return (byParent.get('') ?? []).map((root) => build(root, 0));
}

/**
 * DERIVE — the scheme version a screen should read.
 *
 * Several versions of a scheme coexist by design (reqs/00 D4), so "the current one" is
 * the active one; where none is flagged active the first is used, which is the only
 * choice that is not a guess about ordering the contract does not promise.
 */
export function activeScheme(schemes: readonly SchemeSummary[]): SchemeSummary | undefined {
  return schemes.find((scheme) => scheme.isActive) ?? schemes[0];
}

// ── exceptions ───────────────────────────────────────────────────────────────

/**
 * DERIVE — a free-text severity into the four-value threshold class §12.6 needs.
 *
 * An alert whose severity this build does not recognise is shown as `serious`. That is
 * deliberate and it is not a default: the two failure directions are not symmetric.
 * Downgrading an unknown severity to `warning` hides a breach; dropping the alert hides
 * it completely; overstating it as `critical` trains a planner to ignore critical. The
 * class is visible and labelled either way, so a planner can see it is an alert the queue
 * could not classify.
 */
const SEVERITY_CLASS: Readonly<Record<string, ThresholdClass>> = {
  good: 'good',
  info: 'good',
  low: 'warning',
  warning: 'warning',
  medium: 'serious',
  serious: 'serious',
  high: 'critical',
  critical: 'critical',
};

export const UNCLASSIFIED_SEVERITY: ThresholdClass = 'serious';

export function toThresholdClass(severity: string): ThresholdClass {
  return SEVERITY_CLASS[severity.trim().toLowerCase()] ?? UNCLASSIFIED_SEVERITY;
}

/**
 * DERIVE — a rule identifier into the catalogue key the queue renders.
 *
 * `Exception.typeKey` is a key with no arguments, so a rule the catalogue does not name
 * cannot be rendered as "rule X" without a placeholder the type has no room for. It gets
 * a labelled `unclassified` key instead, and the rule id stays visible in the export.
 * Widening `typeKey` to accept ICU arguments is a `src/planning/` change and therefore a
 * conversation — recorded in the report rather than made here.
 */
// The rules' words live in `@/planning` since the parameter names do too (an exception rule's
// governed key is named by its rule, DEC-111 residuals): one table, never two that drift.
const RULE_KEY: Readonly<Record<string, string>> = EXCEPTION_RULE_LABEL_KEY;

export const UNCLASSIFIED_RULE_KEY = 'foundations.rule.unclassified';

export function toRuleKey(ruleId: string): string {
  return RULE_KEY[ruleId] ?? UNCLASSIFIED_RULE_KEY;
}

/**
 * The decimals an alert's observed value and threshold are shown with, by the rule's measure
 * (bata-platform ADR 2100): weeks of cover to one decimal; stranded units and the cycle's 1-or-0
 * verdict as whole numbers. A measure this table does not name keeps `formatNumber`'s default
 * (two decimals below 1,000) — never the raw float the platform sends (`0.3333333333333333`).
 */
const MEASURE_DECIMALS: Readonly<Record<string, number>> = {
  weeks_of_cover: 1,
  size_gap_stranded_units: 0,
  cycle_tick_failed_or_empty: 0,
};

/** A rule's measure in words — `@/planning` holds the table, so the Workbench names an alert the same way (audit 4). */
export { MEASURE_LABEL_KEY, measureLabelKey } from '@/planning';

/** An alert's observed value or threshold, in the locale, at its measure's precision. */
export function formatAlertValue(value: number, measure: string, locale: string): string {
  const digits = MEASURE_DECIMALS[measure];
  return digits === undefined ? formatNumber(value, locale) : formatNumber(value, locale, { fractionDigits: digits });
}

/**
 * GAP — `AlertSummary` carries no lineage.
 *
 * G2.2 requires every number to answer "where did this come from" with the measure
 * definition, the source batch and the plan version. The alert model publishes the
 * measure and nothing else, so the other four fields are EMPTY rather than plausible:
 * a fabricated batch id is worse than a blank one, because a blank cannot be cited in a
 * review and a fabricated one can.
 */
export function unsourcedLineage(measure: string): Lineage {
  return { measure, definition: '', registerVersion: '', sourceBatch: '', planVersionId: '' };
}

/**
 * An alert's lineage, from the fields `AlertSummary` now publishes.
 *
 * It used to be a GAP: the alert model carried the measure and nothing else, and this row said
 * so with four empty fields. The pinned client now publishes `definitionId`, `metricsVersion`,
 * `batchId` and `planVersionId` on every alert, and the platform's mapper sets all four — the
 * 2026-09-25 audit found the queue still blanking them. A field the platform sends as null
 * stays EMPTY rather than plausible: a blank batch id cannot be cited in a review and a
 * fabricated one can.
 */
export function alertLineage(alert: AlertSummary): Lineage {
  return {
    measure: alert.measure,
    definition: alert.definitionId ?? '',
    registerVersion: alert.metricsVersion ?? '',
    sourceBatch: alert.batchId ?? '',
    planVersionId: alert.planVersionId ?? '',
  };
}

/**
 * TRANSFORM + GAP — an alert into a queue row.
 *
 * `actions` is EMPTY, and that is the honest state: the spec exposes no disposition
 * endpoint, so there is no action this screen could offer that would persist. The queue
 * renders no bulk bar for an alert with no actions rather than offering a control that
 * silently discards a planner's decision (G2 §2.6).
 */
export function toExceptions(alerts: readonly AlertSummary[]): readonly Exception[] {
  return alerts.map((alert) => ({
    id: alert.alertId,
    thresholdClass: toThresholdClass(alert.severity),
    typeKey: toRuleKey(alert.ruleId),
    entityId: alert.subjectId,
    entityLabel: alert.subjectId,
    measure: alert.measure,
    actual: { kind: 'number', value: alert.observed },
    expected: { kind: 'number', value: alert.threshold },
    detectedAt: alert.raisedAt,
    lineage: alertLineage(alert),
    actions: [],
  }));
}

// ── feeds ────────────────────────────────────────────────────────────────────

const HOUR_MS = 3_600_000;

/**
 * DERIVE — certification state.
 *
 * reqs/08 §6 makes certification binary, so `certified` is the only thing that can make a
 * feed green. A feed that is not certified is `quarantined` when it is blocking
 * publication and `awaiting` otherwise. `degraded` is never derived here: the contract
 * exposes nothing that distinguishes "loading but unhealthy" from "not certified yet",
 * and inventing the distinction would put a middle state on a screen that the data does
 * not support.
 */
export function toFeedState(feed: FeedStatus): FeedState {
  if (feed.certified) return 'certified';
  return feed.blocking === true ? 'quarantined' : 'awaiting';
}

/**
 * DERIVE + GAP — a feed row into the certification strip's model.
 *
 * `FeedStatus` publishes `freshnessHours` and no timestamps, while `FeedCertification`
 * requires `lastLoadedAt` and `checkedAt`. `lastLoadedAt` is derived — it is exactly what
 * "freshness hours" means, measured back from when the response was observed — and
 * `checkedAt` IS the observation. A feed whose freshness is not reported gets the
 * observation time and a `freshnessUnknown` label, so the strip never claims a load it
 * cannot evidence.
 *
 * `observedAt` is a parameter rather than `Date.now()` so the mapping is pure and the
 * test asserts a value rather than a range.
 */
export function toFeedCertifications(
  feeds: readonly FeedStatus[],
  observedAt: string,
): readonly FeedCertification[] {
  const observed = new Date(observedAt).getTime();
  return feeds.map((feed) => {
    const state = toFeedState(feed);
    const fresh = typeof feed.freshnessHours === 'number';
    const lastLoadedAt = fresh
      ? new Date(observed - (feed.freshnessHours as number) * HOUR_MS).toISOString()
      : observedAt;
    const failureKey = state === 'certified'
      ? undefined
      : fresh ? 'foundations.feed.awaitingCertification' : 'foundations.feed.freshnessUnknown';
    return {
      feedId: feed.feedId,
      feedLabel: feed.feedId,
      state,
      lastLoadedAt,
      checkedAt: observedAt,
      ...(failureKey === undefined ? {} : { failureKey }),
    };
  });
}

// ── scenarios ────────────────────────────────────────────────────────────────

/**
 * TRANSFORM + GAP — a scenario branch pointer into the switcher's context.
 *
 * Two gaps, both visible on the screen rather than papered over:
 *
 *   * `ScenarioSummary` has no status, so it is DERIVED from `promotedVersionId` — a
 *     scenario that has been promoted into a version is approved, and one that has not
 *     is a draft. The two states the derivation cannot reach (`in-review`, `locked`) are
 *     simply never shown, rather than being guessed at from cell counts;
 *   * `ScenarioSummary` has no recompute timestamp. `lastRecomputedAt` is empty, and the
 *     screen states that the recompute age is not reported instead of printing a date
 *     that would be read as one.
 *
 * `nameKey` arrives from the API as a catalogue key, but it is the PLATFORM's catalogue
 * and this repository's catalogue does not contain it — translating it here would render
 * `⟦…⟧` to a planner. It is shown as the reference datum it is, which is also what G6
 * says a scenario name is.
 */
export function toScenarioContexts(
  scenarios: readonly ScenarioSummary[],
): readonly ScenarioContext[] {
  return scenarios.map((scenario) => ({
    scenarioId: scenario.scenarioId,
    scenarioName: scenario.nameKey,
    baselineVersionId: scenario.baseVersionId,
    baselineVersionLabel: scenario.baseVersionId,
    status: scenario.promotedVersionId ? 'approved' : 'draft',
    lastRecomputedAt: '',
  }));
}

/**
 * DERIVE — the plan a screen should read versions for.
 *
 * GAP: the spec exposes `/foundations/plans/{plan_id}/versions` but no plan directory, so
 * there is no endpoint that answers "which plans exist". `ScenarioSummary` carries
 * `planId`, so the scenario list is the only published route to a plan identifier. That
 * is a derivation, not an invented endpoint, and it is the first thing the next spec
 * version should make unnecessary.
 */
export function planIdsFromScenarios(scenarios: readonly ScenarioSummary[]): readonly string[] {
  return [...new Set(scenarios.map((scenario) => scenario.planId))].sort();
}
